import { Box, HStack, Input, Stack, StackSeparator, Table, Text, VisuallyHidden } from '@chakra-ui/react'
import Papa from 'papaparse'
import type { ParseResult } from 'papaparse'
import { useEffect, useState } from 'react'
import type { FC } from 'react'

import { Checkbox } from '@app/components/ui/checkbox'
import { Field } from '@app/components/ui/field'
import { NativeSelectField, NativeSelectRoot } from '@app/components/ui/native-select'
import PreviewSegments from '@app/pages/metrics/components/csv/previewSegments'
import PreviewTableBody from '@app/pages/metrics/components/csv/previewTableBody'
import PreviewTableHeader from '@app/pages/metrics/components/csv/previewTableHeader'

interface Props {
  file: File
  requireIdField?: boolean
}

const Preview: FC<Props> = ({ file, requireIdField = false }) => {
  const [records, setRecords] = useState<ParseResult>({ data: [], metadata: {} })
  const [containsHeaders, setContainsHeaders] = useState(false)
  const [headers, setHeaders] = useState({})
  const [parsed, setParsed] = useState(false)
  const [usedIndexes, setUsedIndexes] = useState<number[]>([])

  const [dateField, setDateField] = useState('')
  const [valueField, setValueField] = useState('')
  const [idField, setIdField] = useState('')

  const resetFields = () => {
    setRecords({ data: [], metadata: {} })
    setParsed(false)
    setUsedIndexes([])
    setHeaders({})
    setDateField('')
    setValueField('')
    setIdField('')
  }

  useEffect(() => {
    const parseRecords = async () => {
      await Papa.parse(file, {
        header: containsHeaders,
        preview: 5,
        skipEmptyLines: 'greedy',
        complete: (results) => {
          setRecords(results)

          const parsedHeaders = {}
          const parsedIndexes = []

          if (containsHeaders) {
            results?.meta?.fields.forEach((field, idx) => {
              if (field === 'date') {
                setDateField(idx.toString())
                parsedIndexes.push(idx)
              } else if (field === 'value') {
                setValueField(idx.toString())
                parsedIndexes.push(idx)
              } else if (field === 'id' && requireIdField) {
                setIdField(idx.toString())
                parsedIndexes.push(idx)
              }

              parsedHeaders[idx] = field
            })

            setUsedIndexes(parsedIndexes)
          } else {
            Object.keys(results?.data[0]).forEach((field, idx) => (parsedHeaders[idx] = `col${idx}`))
          }

          setHeaders(parsedHeaders)
        }
      })
    }

    if (!records?.data?.length && !parsed && file) {
      parseRecords()
        .then(() => {})
        .finally(() => setParsed(true))
    }
  }, [file, records, containsHeaders, parsed, requireIdField, usedIndexes])

  // Used to reset between file uploads
  useEffect(() => {
    if (!file) {
      resetFields()
    }
  }, [file])

  if (!file) {
    return <Text>Upload a CSV to see a preview and map your data</Text>
  }

  const handleDateFieldChange = (e) => {
    if (!containsHeaders) {
      const changeHash = e.currentTarget.value ? { [e.currentTarget.value]: 'date' } : {}
      const dateIndex = Object.values(headers).findIndex((key) => key === 'date')
      const dateHash = dateIndex > -1 ? { [dateIndex]: `col${dateIndex}` } : {}

      setHeaders({ ...headers, ...dateHash, ...changeHash })
    }

    const newIndex = e.currentTarget.value !== '' ? [parseInt(e.currentTarget.value)] : []
    if (dateField !== '') {
      setUsedIndexes([...usedIndexes.filter((idx) => idx !== parseInt(dateField)).concat(newIndex)])
    } else {
      setUsedIndexes([...usedIndexes.concat(newIndex)])
    }

    setDateField(e.currentTarget.value)
  }

  const handleValueFieldChange = (e) => {
    if (!containsHeaders) {
      const changeHash = e.currentTarget.value ? { [e.currentTarget.value]: 'value' } : {}
      const valueIndex = Object.values(headers).findIndex((key) => key === 'value')
      const valueHash = valueIndex > -1 ? { [valueIndex]: `col${valueIndex}` } : {}

      setHeaders({ ...headers, ...valueHash, ...changeHash })
    }

    const newIndex = e.currentTarget.value !== '' ? [parseInt(e.currentTarget.value)] : []
    if (valueField !== '') {
      setUsedIndexes([...usedIndexes.filter((idx) => idx !== parseInt(valueField)).concat(newIndex)])
    } else {
      setUsedIndexes([...usedIndexes.concat(newIndex)])
    }

    setValueField(e.currentTarget.value)
  }

  const handleIdFieldChange = (e) => {
    if (!containsHeaders) {
      const changeHash = e.currentTarget.value ? { [e.currentTarget.value]: 'id' } : {}
      const idIndex = Object.values(headers).findIndex((key) => key === 'id')
      const idHash = idIndex > -1 ? { [idIndex]: `col${idIndex}` } : {}

      setHeaders({ ...headers, ...idHash, ...changeHash })
    }

    const newIndex = e.currentTarget.value !== '' ? [parseInt(e.currentTarget.value)] : []
    if (idField !== '') {
      setUsedIndexes([...usedIndexes.filter((idx) => idx !== parseInt(idField)).concat(newIndex)])
    } else {
      setUsedIndexes([...usedIndexes.concat(newIndex)])
    }

    setIdField(e.currentTarget.value)
  }

  return (
    <Stack gap={4} separator={<StackSeparator />}>
      <Stack gap={4}>
        <HStack>
          <Field label="Date field" required invalid={dateField === ''}>
            <NativeSelectRoot>
              <NativeSelectField value={dateField.toString()} onChange={handleDateFieldChange}>
                <option value="">Pick a column</option>
                {Object.keys(headers).map((key) => (
                  <option key={key} value={key} disabled={usedIndexes.includes(parseInt(key))}>
                    {headers[key]}
                  </option>
                ))}
              </NativeSelectField>
              <VisuallyHidden>
                <Input name="dateIndex" value={dateField} readOnly />
              </VisuallyHidden>
            </NativeSelectRoot>
          </Field>
          <Field label="Value field" required invalid={valueField === ''}>
            <NativeSelectRoot>
              <NativeSelectField value={valueField.toString()} onChange={handleValueFieldChange}>
                <option value="">Pick a column</option>
                {Object.keys(headers).map((key) => (
                  <option key={key} value={key} disabled={usedIndexes.includes(parseInt(key))}>
                    {headers[key]}
                  </option>
                ))}
              </NativeSelectField>
              <VisuallyHidden>
                <Input name="valueIndex" value={valueField} readOnly />
              </VisuallyHidden>
            </NativeSelectRoot>
          </Field>
          {requireIdField && (
            <Field label="ID field" required invalid={idField === ''}>
              <NativeSelectRoot>
                <NativeSelectField value={idField.toString()} onChange={handleIdFieldChange}>
                  <option value="">Pick a column</option>
                  {Object.keys(headers).map((key) => (
                    <option key={key} value={key} disabled={usedIndexes.includes(parseInt(key))}>
                      {headers[key]}
                    </option>
                  ))}
                </NativeSelectField>
                <VisuallyHidden>
                  <Input name="idIndex" value={idField} readOnly />
                </VisuallyHidden>
              </NativeSelectRoot>
            </Field>
          )}
        </HStack>
        <PreviewSegments
          headers={headers}
          setHeaders={setHeaders}
          usedIndexes={usedIndexes}
          setUsedIndexes={setUsedIndexes}
        />
      </Stack>
      <Box css={{ '--field-label-width': 'auto' }}>
        <Field label="Data contains headers" orientation="horizontal">
          <Checkbox
            name="headers"
            checked={containsHeaders}
            onCheckedChange={(e) => {
              resetFields()
              setContainsHeaders(!!e.checked)
            }}
          ></Checkbox>
        </Field>
      </Box>

      <Table.ScrollArea borderWidth="1px" maxW="100%">
        <Table.Root striped variant="outline" interactive>
          <PreviewTableHeader headers={headers} />
          <PreviewTableBody data={records?.data} />
        </Table.Root>
      </Table.ScrollArea>
    </Stack>
  )
}

export default Preview
